import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react'

import { detectLang, pick, setLang as persist, translate, type Lang, type TranslateKey } from './lib/i18n'

interface Ctx {
  lang: Lang
  t: (key: TranslateKey, vars?: Record<string, string | number>) => string
  pickText: (text: { de?: string; en?: string } | undefined) => string
  switchTo: (lang: Lang) => void
}

const LangContext = createContext<Ctx | null>(null)

export function LangProvider({ children, initial }: { children: ReactNode; initial?: Lang }) {
  const [lang, setLangState] = useState<Lang>(() => initial ?? detectLang())

  const switchTo = useCallback((next: Lang) => {
    persist(next)
    setLangState(next)
    document.documentElement.lang = next
  }, [])

  const value = useMemo<Ctx>(
    () => ({
      lang,
      t: (key, vars) => translate(lang, key, vars),
      pickText: (text) => pick(text, lang),
      switchTo,
    }),
    [lang, switchTo],
  )
  return <LangContext.Provider value={value}>{children}</LangContext.Provider>
}

export function useLang(): Ctx {
  const ctx = useContext(LangContext)
  if (!ctx) throw new Error('useLang outside LangProvider')
  return ctx
}
